import { useState, useCallback, useEffect, useRef } from 'react';

interface LightboxProps {
  images: string[];
  initialIndex: number;
  alt: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function Lightbox({ images, initialIndex, alt, onClose, onPrev, onNext }: LightboxProps) {
  const [zoomed, setZoomed] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const imgRef = useRef<HTMLImageElement>(null);

  const currentSrc = images[initialIndex] || '';
  const hasMultiple = images.length > 1;

  // Reset zoom/position on image change
  useEffect(() => {
    setZoomed(false);
    setPosition({ x: 0, y: 0 });
  }, [initialIndex]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (zoomed) { setZoomed(false); setPosition({ x: 0, y: 0 }); }
        else onClose();
      }
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext, zoomed]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const toggleZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomed((z) => {
      if (z) { setPosition({ x: 0, y: 0 }); return false; }
      return true;
    });
  }, []);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (zoomed) { setZoomed(false); setPosition({ x: 0, y: 0 }); }
      else onClose();
    }
  }, [onClose, zoomed]);

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!zoomed) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, posX: position.x, posY: position.y };
  }, [zoomed, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPosition({ x: dragStart.current.posX + dx, y: dragStart.current.posY + dy });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    if (e.deltaY < 0 && !zoomed) setZoomed(true);
  }, [zoomed]);

  const handleNavClick = useCallback((e: React.MouseEvent, fn: () => void) => {
    e.stopPropagation();
    fn();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/92 backdrop-blur-sm flex flex-col"
      onClick={handleBackdropClick}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0 z-10">
        <div className="flex items-center gap-3">
          {hasMultiple && (
            <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-white text-xs font-medium">
              {initialIndex + 1} / {images.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleZoom}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            title={zoomed ? 'Fit to screen' : 'Zoom in'}
          >
            <i className={zoomed ? 'ri-zoom-out-line' : 'ri-zoom-in-line'} />
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            title="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 flex items-center justify-center relative overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ cursor: zoomed ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
      >
        <img
          ref={imgRef}
          src={currentSrc}
          alt={`${alt} — Photo ${initialIndex + 1}`}
          draggable={false}
          className="select-none transition-transform duration-300 ease-out"
          style={{
            maxWidth: zoomed ? 'none' : '90vw',
            maxHeight: zoomed ? 'none' : '85vh',
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoomed ? 2 : 1})`,
            objectFit: zoomed ? 'none' : 'contain',
          }}
          onClick={toggleZoom}
          onDoubleClick={(e) => {
            e.stopPropagation();
            toggleZoom(e);
          }}
        />
      </div>

      {/* Nav arrows — outside the image area so they don't interfere with drag */}
      {hasMultiple && (
        <>
          <button
            onClick={(e) => handleNavClick(e, onPrev)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur transition-all cursor-pointer z-10"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>
          <button
            onClick={(e) => handleNavClick(e, onNext)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur transition-all cursor-pointer z-10"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>
        </>
      )}

      {/* Bottom thumbnails */}
      {hasMultiple && (
        <div className="flex items-center justify-center gap-2 px-4 py-3 shrink-0 z-10">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                if (i < initialIndex) {
                  // We need to go to this specific index — call onPrev/onNext repeatedly
                  const diff = initialIndex - i;
                  for (let d = 0; d < diff; d++) onPrev();
                } else if (i > initialIndex) {
                  const diff = i - initialIndex;
                  for (let d = 0; d < diff; d++) onNext();
                }
              }}
              className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all cursor-pointer shrink-0 ${
                i === initialIndex
                  ? 'border-white opacity-100 ring-2 ring-white/30'
                  : 'border-transparent opacity-50 hover:opacity-80 hover:border-white/40'
              }`}
            >
              <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}